import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useState } from 'react';

// Define the shape of the data your message templates will receive.
export type MessageTemplateContext = {
	message: string;
	link?: {
		href: string;
		text: string;
	};
};

export type PlacementRenderProps = {
	onDismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: `quick-find-spotlight`,
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<MessageTemplateContext>>;
	}>('/');

	// local state for managing messages on the client-side to enable optimistic dismissals.
	const [localMessageState, setLocalMessageState] = useState<
		Array<EligibleMessage<MessageTemplateContext>> | undefined
	>(undefined);

	useEffect(() => {
		if (data?.messages) {
			setLocalMessageState(data.messages);
		}
	}, [data?.messages]);

	const handleDismiss = useCallback((messageInstanceId: string) => {
		setLocalMessageState((currentMessages = []) =>
			currentMessages.filter((m) => m.messageInstanceId !== messageInstanceId),
		);
	}, []);

	if (error) {
		throw error;
	}

	return (
		<>
			{!isLoading &&
				localMessageState?.map((props) => (
					<Messages.render
						key={props.messageInstanceId}
						{...props}
						onDismiss={() => handleDismiss(props.messageInstanceId)}
					/>
				))}
		</>
	);
});
